import { ChangeDetectionStrategy, Component, computed, effect, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PmsType, ShijiPmsSetup } from '../../../../../types/pms';

@Component({
    selector: 'juyo-shiji-connect',
    styleUrl: './shiji.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
    template: `
        <h3 class="step-title">Connect to Shiji</h3>
        <p class="step-subtitle">
            You'll need to create an API user inside Shiji Daylight, then share the credentials below so Juyo can
            connect.
        </p>

        <div class="field-grid">
            <div class="field">
                <label class="field-label" for="shiji-tenant-name">TENANT NAME <span class="required">*</span></label>
                <input
                    id="shiji-tenant-name"
                    type="text"
                    class="field-input"
                    placeholder="e.g. XX"
                    [ngModel]="tenantName()"
                    (ngModelChange)="tenantName.set($event.toUpperCase())" />
                <p class="field-hint">
                    What you type in the <strong>Tenant</strong> field when logging in to Shiji Daylight.
                </p>
            </div>

            <div class="field">
                <label class="field-label" for="shiji-property-id">PROPERTY ID <span class="required">*</span></label>
                <input
                    id="shiji-property-id"
                    type="text"
                    class="field-input"
                    placeholder="e.g. aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
                    [ngModel]="propertyId()"
                    (ngModelChange)="propertyId.set($event)" />
                <p class="field-hint">
                    In Daylight: <strong>Main Menu → Configuration → Property Details</strong>. Copy the value between
                    <code>/properties/</code> and <code>/details/</code> in the browser URL.
                </p>
            </div>

            <div class="field">
                <label class="field-label" for="shiji-username">USERNAME <span class="required">*</span></label>
                <input
                    id="shiji-username"
                    type="text"
                    class="field-input"
                    placeholder="e.g. XX"
                    [ngModel]="username()"
                    (ngModelChange)="username.set($event)" />
                <p class="field-hint">
                    In Daylight: <strong>Main Menu → Configuration → User List → Create New User</strong>. Assign
                    permission <strong>Data API → Access Data API</strong>.
                </p>
            </div>

            <div class="field">
                <label class="field-label" for="shiji-password">PASSWORD <span class="required">*</span></label>
                <div class="field-input-wrapper">
                    <input
                        id="shiji-password"
                        [type]="showPassword() ? 'text' : 'password'"
                        class="field-input field-input--password"
                        placeholder="Paste the password"
                        [ngModel]="password()"
                        (ngModelChange)="password.set($event)" />
                    <button
                        type="button"
                        class="password-toggle"
                        [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
                        [attr.aria-pressed]="showPassword()"
                        (click)="showPassword.set(!showPassword())">
                        <span class="material-symbols-outlined">
                            {{ showPassword() ? 'visibility_off' : 'visibility' }}
                        </span>
                    </button>
                </div>
                <p class="field-hint">Shared securely with the onboarding team.</p>
            </div>
        </div>

        <div class="next-box">
            <span class="material-symbols-outlined next-icon">info</span>
            <div class="next-content">
                <p class="next-title">What happens next</p>
                <p class="next-body">
                    Juyo configures the connection with your credentials, validates API access, and activates the
                    integration. We'll email you if we need additional information.
                </p>
            </div>
        </div>
    `,
})
export class ShijiConnectComponent {
    public valueChange = output<ShijiPmsSetup | null>();

    protected tenantName = signal('');
    protected propertyId = signal('');
    protected username = signal('');
    protected password = signal('');
    protected showPassword = signal(false);

    private setup = computed<ShijiPmsSetup | null>(() => {
        const tenantName = this.tenantName().trim();
        const propertyId = this.propertyId().trim();
        const username = this.username().trim();
        const password = this.password();

        if (!tenantName || !propertyId || !username || !password) {
            return null;
        }

        return { type: PmsType.Shiji, tenantName, propertyId, username, password };
    });

    constructor() {
        effect(() => this.valueChange.emit(this.setup()));
    }
}
